import { Button, FormHelperText, MenuItem, Radio, TextField } from '@mui/material';
import { Box } from '@mui/system';
import type { useFormik } from 'formik';
import { matchIsValidColor } from 'mui-color-input';
import type { ComponentProps } from 'react';
import { useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { array, boolean, object, string } from 'yup';
import { DetailColorInput } from '~/components/forms/DetailColorInput';
import { DetailRadioGroup, DetailSwitch, DetailTextField } from '~/components/forms/details';
import {
  SettingDescription,
  SettingFieldRequired,
  SettingInputWrapper,
  SettingLabelWrapper,
  SettingName,
  SettingsBody,
  SettingsData,
  SettingsHead,
} from '~/components/settings';
import { useAppContext } from '~/contexts';
import { ChannelEncryptionKind } from '~/generated/graphql';
import { toDataUri } from '~/lib/file';
import { i18n } from '~/lib/i18n';
import { formatEncryption } from '../lib/formatters';

export interface ChannelFormValues {
  backgroundColor: string;
  enabledForLiveStreaming: boolean;
  encryption: ChannelEncryptionKind;
  jpegxsEnabled: boolean;
  name: string;
  number: string;
  source?: string;
  thumbnailUri: string;
  url: string;
  urls: readonly string[];
  outputUrl: string;
  outputUrls: readonly string[];
}

const encyptionOptions = [
  ChannelEncryptionKind.None,
  ChannelEncryptionKind.Aes_128,
  ChannelEncryptionKind.Passthrough,
  // ChannelEncryptionKind.ProIdiom,
];

export const isValidChannelUrl = (url: string) =>
  /^(udp|rtp|https?|rtsp):\/\/[^\s/$.?#].[^\s]*$/i.test(url) || /^hdmi:\/\/\d+$/i.test(url);

const incrementUrlPort = (url: string, increment: number): string => {
  if (!url) return '';
  try {
    const urlObj = new URL(url);
    const currentPort = urlObj.port ? parseInt(urlObj.port) : 0;
    const newPort = currentPort + increment;
    urlObj.port = newPort.toString();
    return urlObj.toString();
  } catch {
    return url;
  }
};

const filterUrls = (urls: string[]) => urls.filter((url) => url && url.trim() !== '');

export const channelSchema = object({
  backgroundColor: string()
    .test('is-color', i18n.t('errors.validation.color'), (value) => {
      return value != null && matchIsValidColor(value);
    })
    // eslint-disable-next-line no-restricted-syntax -- user-picked value, not a theme color
    .default('#000000')
    .required(i18n.t('errors.validation.required'))
    .label('Background color'),
  enabledForLiveStreaming: boolean()
    .required()
    .label(i18n.t('pages.channels.form.fields.enabled_for_live_streaming.label')),
  encryption: string()
    .required(i18n.t('errors.validation.required'))
    .oneOf(Object.values(ChannelEncryptionKind))
    .label('Encryption'),
  jpegxsEnabled: boolean()
    .required()
    .label(i18n.t('pages.channels.form.fields.jpegxs_enabled.label')),
  name: string().required(i18n.t('errors.validation.required')).label('Name'),
  number: string().required(i18n.t('errors.validation.required')).label('Channel number'),
  source: string().optional().label('Source'),
  thumbnailUri: string().required(i18n.t('errors.validation.required')).label('Thumbnail'),
  url: string().required(i18n.t('errors.validation.required')).label('URL'),
  urls: array()
    .min(1, i18n.t('errors.validation.min_one_url'))
    .max(5, i18n.t('errors.validation.max_five_urls'))
    .required(i18n.t('errors.validation.urls_required'))
    .test('all-valid-urls', i18n.t('errors.validation.all_urls_valid'), (value) => {
      const filteredUrls = value.filter((url: string) => url && url.trim() !== '');
      return filteredUrls.every(isValidChannelUrl);
    })
    .test('at-least-one-valid-url', i18n.t('errors.validation.min_one_valid_url'), (value) => {
      const filteredUrls = value.filter((url: string) => url && url.trim() !== '');
      return filteredUrls.some(isValidChannelUrl);
    })
    .test('unique-urls', i18n.t('errors.validation.urls_unique'), (value) => {
      const filteredUrls = value.filter((url: string) => url && url.trim() !== '');
      const uniqueUrls = new Set(filteredUrls);
      return uniqueUrls.size === filteredUrls.length;
    }),
  outputUrl: string()
    .when('encryption', {
      is: (encryption: ChannelEncryptionKind) => encryption !== ChannelEncryptionKind.None,
      then: (schema) => schema.required(i18n.t('errors.validation.output_url_required_encryption')),
      otherwise: (schema) => schema.notRequired(),
    })
    .test(
      'valid-output-url',
      i18n.t('errors.validation.output_url_must_be_valid'),
      function (value) {
        const parent = this.parent as ChannelFormValues;
        const { url, outputUrls } = parent;
        if (!value) return true; // Let required validation handle empty values

        // Allow if it's in the outputUrls array
        if (outputUrls.includes(value)) return true;

        // Allow if it's the input URL with port +1000
        const inputUrlPlusPort = incrementUrlPort(url, 1000);
        if (value === inputUrlPlusPort) return true;

        return false;
      },
    )
    .label('Output URL'),
  outputUrls: array().when('encryption', {
    is: (encryption: ChannelEncryptionKind) => encryption !== ChannelEncryptionKind.None,
    then: (schema) =>
      schema
        .max(5, i18n.t('errors.validation.max_five_urls'))
        .required(i18n.t('errors.validation.urls_required'))
        .test(
          'min-urls-or-input-plus-port',
          i18n.t('errors.validation.min_one_url'),
          function (value: string[]) {
            const parent = this.parent as ChannelFormValues;
            const { url, outputUrl } = parent;
            const filteredUrls = filterUrls(value);

            // Allow empty array if outputUrl is the input URL +1000
            const inputUrlPlusPort = incrementUrlPort(url, 1000);
            if (outputUrl === inputUrlPlusPort && filteredUrls.length === 0) {
              return true;
            }

            // Otherwise require at least 1 URL
            return filteredUrls.length >= 1;
          },
        )
        .test('all-valid-urls', i18n.t('errors.validation.all_urls_valid'), (value: string[]) => {
          const filteredUrls = filterUrls(value);
          if (filteredUrls.length === 0) return true; // Skip if empty (handled by min-urls-or-input-plus-port)
          return filteredUrls.every(isValidChannelUrl);
        })
        .test(
          'at-least-one-valid-url',
          i18n.t('errors.validation.min_one_valid_url'),
          function (value: string[]) {
            const parent = this.parent as ChannelFormValues;
            const { url, outputUrl } = parent;
            const filteredUrls = filterUrls(value);

            // Allow empty array if outputUrl is the input URL +1000
            const inputUrlPlusPort = incrementUrlPort(url, 1000);
            if (outputUrl === inputUrlPlusPort && filteredUrls.length === 0) {
              return true;
            }

            if (filteredUrls.length === 0) return false;
            return filteredUrls.some(isValidChannelUrl);
          },
        )
        .test('unique-urls', i18n.t('errors.validation.urls_unique'), function (value: string[]) {
          const parent = this.parent as ChannelFormValues;
          const { url } = parent;
          const filteredUrls = filterUrls(value);
          if (filteredUrls.length === 0) return true; // Skip if empty

          // Include the autogenerated input URL + port 1000 in the uniqueness check
          const inputUrlPlusPort = incrementUrlPort(url, 1000);
          const allUrls = inputUrlPlusPort ? [...filteredUrls, inputUrlPlusPort] : filteredUrls;
          const uniqueUrls = new Set(allUrls);
          return uniqueUrls.size === allUrls.length;
        }),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export interface ChannelFormProps<T extends ChannelFormValues>
  extends Omit<ComponentProps<'form'>, 'onSubmit'> {
  formik: ReturnType<typeof useFormik<T>>;
  formId: string;
  thumbnailFileUri?: string | undefined;
}

export const ChannelForm: <T extends ChannelFormValues>(
  props: ChannelFormProps<T>,
) => JSX.Element = <T extends ChannelFormValues>({
  formik,
  formId,
  thumbnailFileUri,
}: ChannelFormProps<T>) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const { getInputProps, open: selectFile } = useDropzone({
    accept: 'image/*',
    multiple: false,
    onDrop: async (accepted, _rejected, _event) => {
      if (accepted.length !== 1) return;
      const thumbnailUri = await toDataUri(accepted[0]);
      await formik.setFieldValue('thumbnailUri', thumbnailUri, true);
    },
  });

  const imgSrc = formik.values.thumbnailUri.trim() || thumbnailFileUri;

  const selectedUrlIndex = useMemo(() => {
    if (!formik.values.url) return -1;
    return formik.values.urls.findIndex((url) => url === formik.values.url);
  }, [formik.values.urls, formik.values.url]);

  const selectedOutputIndex = useMemo(() => {
    if (!formik.values.outputUrl) return -1;
    return formik.values.outputUrls.findIndex((url) => url === formik.values.outputUrl);
  }, [formik.values.outputUrls, formik.values.outputUrl]);

  const inputUrlPlusPort = useMemo(() => {
    return incrementUrlPort(formik.values.url, 1000);
  }, [formik.values.url]);

  return (
    <Box
      component="form"
      id={formId}
      onSubmit={formik.handleSubmit}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <Box>
        <SettingsHead>{t('common.labels.general')}</SettingsHead>
        <SettingsBody>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>
                {t('common.labels.name')}
                <SettingFieldRequired>*</SettingFieldRequired>
              </SettingName>
              <SettingDescription>
                {t('pages.channels.form.fields.name.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextField
                autoFocus
                disabled={formik.isSubmitting}
                error={formik.touched.name && !!formik.errors.name}
                fullWidth
                helperText={(formik.touched.name && formik.errors.name) || ' '}
                name="name"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.name}
              />
            </SettingInputWrapper>
          </SettingsData>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>
                {t('pages.channels.form.fields.number.label')}
                <SettingFieldRequired>*</SettingFieldRequired>
              </SettingName>
              <SettingDescription>
                {t('pages.channels.form.fields.number.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextField
                disabled={formik.isSubmitting}
                error={formik.touched.number && !!formik.errors.number}
                fullWidth
                helperText={(formik.touched.number && formik.errors.number) || ' '}
                name="number"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.number}
              />
            </SettingInputWrapper>
          </SettingsData>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>
                {t('pages.channels.form.fields.background_color.label')}
                <SettingFieldRequired>*</SettingFieldRequired>
              </SettingName>
              <SettingDescription>
                {t('pages.channels.form.fields.background_color.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailColorInput
                error={formik.touched.backgroundColor && !!formik.errors.backgroundColor}
                format="hex"
                helperText={
                  (formik.touched.backgroundColor && formik.errors.backgroundColor) || ' '
                }
                name="backgroundColor"
                onBlur={formik.handleBlur}
                onChange={(color) => formik.setFieldValue('backgroundColor', color, true)}
                value={formik.values.backgroundColor}
              />
            </SettingInputWrapper>
          </SettingsData>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>
                {t('common.labels.thumbnail')}
                <SettingFieldRequired>*</SettingFieldRequired>
              </SettingName>
              <SettingDescription>
                {t('pages.channels.form.fields.thumbnail.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <Box display="flex" flexDirection="row" gap={2}>
                {imgSrc && (
                  <Box
                    alignItems="center"
                    alignSelf="flex-start"
                    display="flex"
                    flexShrink={0}
                    height="8rem"
                    justifyContent="center"
                    sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
                    width="8rem"
                  >
                    <img
                      alt={t('common.labels.current_thumbnail')}
                      src={imgSrc}
                      style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                    />
                  </Box>
                )}
                <Box display="flex" flexDirection="column" flexGrow={1} gap={2}>
                  <input {...getInputProps()} />
                  <div>
                    <Button onClick={selectFile} variant="outlined">
                      {t('pages.channels.form.fields.thumbnail.upload_button')}
                    </Button>
                  </div>
                  <Box display="flex" flexDirection="column">
                    <span>{t('pages.channels.form.fields.thumbnail.url_hint')}</span>
                    <TextField
                      fullWidth
                      inputProps={{
                        'aria-label': t('pages.channels.form.fields.thumbnail.url_hint'),
                      }}
                      name="thumbnailUri"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      size="small"
                    />
                  </Box>
                </Box>
              </Box>
            </SettingInputWrapper>
          </SettingsData>
          <SettingsData
            className={currentNetwork.settings.enableLiveChannelStreaming ? '' : 'last'}
          >
            <SettingLabelWrapper>
              <SettingName>{t('common.labels.source')}</SettingName>
              <SettingDescription>
                {t('pages.channels.form.fields.source.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextField
                disabled={formik.isSubmitting}
                error={formik.touched.source && !!formik.errors.source}
                fullWidth
                helperText={(formik.touched.source && formik.errors.source) || ' '}
                name="source"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.source}
              />
            </SettingInputWrapper>
          </SettingsData>
          {currentNetwork.settings.enableLiveChannelStreaming && (
            <SettingsData className="last">
              <SettingLabelWrapper>
                <SettingName>
                  {t('pages.channels.form.fields.enabled_for_live_streaming.label')}
                </SettingName>
                <SettingDescription>
                  {t('pages.channels.form.fields.enabled_for_live_streaming.description')}
                </SettingDescription>
              </SettingLabelWrapper>
              <SettingInputWrapper>
                <DetailSwitch
                  checked={formik.values.enabledForLiveStreaming}
                  disabled={formik.isSubmitting}
                  name="enabledForLiveStreaming"
                  onChange={formik.handleChange}
                />
              </SettingInputWrapper>
            </SettingsData>
          )}
        </SettingsBody>
      </Box>

      <Box>
        <SettingsHead>{t('pages.channels.form.sections.streams')}</SettingsHead>
        <SettingsBody>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>
                {t('pages.channels.form.fields.encryption.label')}
                <SettingFieldRequired>*</SettingFieldRequired>
              </SettingName>
              <SettingDescription>
                {t('pages.channels.form.fields.encryption.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailTextField
                disabled={formik.isSubmitting}
                error={formik.touched.encryption && !!formik.errors.encryption}
                fullWidth
                helperText={(formik.touched.encryption && formik.errors.encryption) || ' '}
                name="encryption"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                select
                value={formik.values.encryption}
              >
                {encyptionOptions.map((kind) => (
                  <MenuItem key={kind} value={kind}>
                    {formatEncryption(kind)}
                  </MenuItem>
                ))}
              </DetailTextField>
            </SettingInputWrapper>
          </SettingsData>
          <SettingsData>
            <SettingLabelWrapper>
              <SettingName>{t('pages.channels.form.fields.jpegxs_enabled.label')}</SettingName>
              <SettingDescription>
                {t('pages.channels.form.fields.jpegxs_enabled.description')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailSwitch
                checked={formik.values.jpegxsEnabled}
                disabled={formik.isSubmitting}
                name="jpegxsEnabled"
                onChange={formik.handleChange}
              />
            </SettingInputWrapper>
          </SettingsData>
          <SettingsData className="last">
            <SettingLabelWrapper>
              <SettingName>
                {t('pages.channels.form.fields.urls.label')}
                <SettingFieldRequired>*</SettingFieldRequired>
              </SettingName>
              <SettingDescription>
                {formik.values.encryption === ChannelEncryptionKind.None
                  ? t('pages.channels.form.fields.urls.description_none')
                  : t('pages.channels.form.fields.urls.description_encrypted')}
              </SettingDescription>
            </SettingLabelWrapper>
            <SettingInputWrapper>
              <DetailRadioGroup
                sx={{
                  marginBottom: '16px',
                  width: '100%',
                }}
                value={formik.values.url}
              >
                {[0, 1, 2, 3, 4].map((index) => (
                  // A plain row rather than FormControlLabel: a label holding two inputs names
                  // neither, so each control carries its own name.
                  <Box key={`channel-url-${index}`} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Radio
                      checked={selectedUrlIndex === index}
                      disabled={!formik.values.urls[index]}
                      inputProps={{
                        'aria-label': t('pages.channels.form.fields.urls.use_url_label', {
                          number: index + 1,
                        }),
                      }}
                      onChange={async (_e) => {
                        await formik.setFieldValue('url', formik.values.urls[index], true);
                      }}
                    />
                    <TextField
                      value={formik.values.urls[index]}
                      onChange={async (e) => {
                        const urls = [...formik.values.urls];
                        urls[index] = e.target.value;
                        await formik.setFieldValue('urls', urls, true);
                        if (selectedUrlIndex === index)
                          await formik.setFieldValue('url', e.target.value, true);
                      }}
                      inputProps={{
                        'aria-label': t('pages.channels.form.fields.urls.url_label', {
                          number: index + 1,
                        }),
                      }}
                      placeholder={t('pages.channels.form.fields.urls.placeholder')}
                      size="small"
                      fullWidth
                    />
                  </Box>
                ))}
                <FormHelperText>
                  {formik.errors.urls ? (
                    <Box component="span" sx={{ color: 'error.main' }}>
                      {formik.errors.urls}
                    </Box>
                  ) : (
                    t('pages.channels.form.fields.urls.helper_text')
                  )}
                </FormHelperText>
              </DetailRadioGroup>
            </SettingInputWrapper>
          </SettingsData>
          {formik.values.encryption !== ChannelEncryptionKind.None && (
            <SettingsData className="last">
              <SettingLabelWrapper>
                <SettingName>
                  {t('pages.channels.form.fields.output_urls.label')}
                  <SettingFieldRequired>*</SettingFieldRequired>
                </SettingName>
                <SettingDescription>
                  {formik.values.encryption === ChannelEncryptionKind.Aes_128
                    ? t('pages.channels.form.fields.output_urls.description_aes')
                    : t('pages.channels.form.fields.output_urls.description_other')}
                </SettingDescription>
              </SettingLabelWrapper>
              <SettingInputWrapper>
                <DetailRadioGroup
                  sx={{
                    marginBottom: '16px',
                    width: '100%',
                  }}
                  value={formik.values.outputUrl}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Radio
                      checked={formik.values.outputUrl === inputUrlPlusPort}
                      disabled={!inputUrlPlusPort}
                      inputProps={{
                        'aria-label': t(
                          'pages.channels.form.fields.output_urls.use_input_plus_port_label',
                        ),
                      }}
                      onChange={async (_e) => {
                        await formik.setFieldValue('outputUrl', inputUrlPlusPort, true);
                      }}
                    />
                    <TextField
                      value={inputUrlPlusPort}
                      disabled
                      inputProps={{
                        'aria-label': t(
                          'pages.channels.form.fields.output_urls.input_plus_port_label',
                        ),
                      }}
                      placeholder={t('pages.channels.form.fields.output_urls.placeholder')}
                      size="small"
                      fullWidth
                    />
                  </Box>
                  {[0, 1, 2, 3, 4].map((index) => (
                    <Box
                      key={`channel-output-url-${index}`}
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <Radio
                        checked={selectedOutputIndex === index}
                        disabled={!formik.values.outputUrls[index]}
                        inputProps={{
                          'aria-label': t('pages.channels.form.fields.output_urls.use_url_label', {
                            number: index + 1,
                          }),
                        }}
                        onChange={async (_e) => {
                          await formik.setFieldValue(
                            'outputUrl',
                            formik.values.outputUrls[index],
                            true,
                          );
                        }}
                      />
                      <TextField
                        value={formik.values.outputUrls[index]}
                        onChange={async (e) => {
                          const urls = [...formik.values.outputUrls];
                          urls[index] = e.target.value;
                          await formik.setFieldValue('outputUrls', urls, true);
                          if (selectedOutputIndex === index)
                            await formik.setFieldValue('outputUrl', e.target.value, true);
                        }}
                        inputProps={{
                          'aria-label': t('pages.channels.form.fields.output_urls.url_label', {
                            number: index + 1,
                          }),
                        }}
                        placeholder={t('pages.channels.form.fields.output_urls.placeholder')}
                        size="small"
                        fullWidth
                      />
                    </Box>
                  ))}
                  <FormHelperText>
                    {formik.errors.outputUrls ? (
                      <Box component="span" sx={{ color: 'error.main' }}>
                        {formik.errors.outputUrls}
                      </Box>
                    ) : (
                      t('pages.channels.form.fields.output_urls.helper_text')
                    )}
                  </FormHelperText>
                </DetailRadioGroup>
              </SettingInputWrapper>
            </SettingsData>
          )}
        </SettingsBody>
      </Box>
    </Box>
  );
};
